import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CustomMessageService } from '@app/shared/components/custom-message/custom-message.service';
import { LoggerService } from '@app/logger/logger.service';
import { InterventionsService } from '@app/shared/services/entities/interventions.service';
import { PatientsService } from '@app/shared/services/entities/patients.service';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MaterialModule } from '@app/material.module';
const log = new LoggerService('INTERVENTION DIALOG COMPONENT');

@Component({
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MaterialModule,
  ],
  selector: 'app-interventions-dialog',
  templateUrl: './interventions-dialog.component.html',
  styleUrls: ['./interventions-dialog.component.scss']
})
export class InterventionsDialogComponent implements OnInit {

  public interventionForm: FormGroup;
  public doctorList = [];
  public doctorFilteredList = [];
  public doctorObject = [];
  public procedureList = [];
  public procedureFilteredList = [];
  public procedureObject = [];
  public specialtyList = [];
  public specialtyObject = [];
  public operationRoomList = [];
  public operationRoomObject = [];
  public patientList = [];
  public patientObject = [];
  public productList = [];
  public productObject = [];
  public placesList = [];
  public placesObject = [];
  public contractList = [];
  public record = null;
  public selectedPatient: any = null;
  public isEditingPatient: boolean = false;
  public isCreatingPatient: boolean = false;

  // Para el autocomplete
  public patientSearchControl = new FormControl('');
  public filteredPatients: Observable<any[]>;

  constructor(
    private formBuilder: FormBuilder,
    public dialogRef: MatDialogRef<InterventionsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private customMessageService: CustomMessageService,
    private interventionsService: InterventionsService,
    private patientsService: PatientsService,
  ) { }

  ngOnInit() {
    log.debug(this.data)
    this.doctorList = this.data['doctor'];
    this.operationRoomList = this.data['operationRoom'];
    this.patientList = this.data['patient'];
    this.placesList = this.data['places'];
    this.procedureList = this.data['procedure'];
    this.productList = this.data['product'];
    this.specialtyList = this.data['specialty'];
    this.doctorObject = this.data['doctorObj'];
    this.operationRoomObject = this.data['operationRoomObj'];
    this.patientObject = this.data['patientObj'];
    this.placesObject = this.data['placesObj'];
    this.procedureObject = this.data['procedureObj'];
    this.productObject = this.data['productObj'];
    this.specialtyObject = this.data['specialtyObj'];
    this.contractList = this.data['contract'];
    this.record = this.data.record;

    this.interventionForm = this.formBuilder.group({
      interventionDatetime: [this.data.type == 'update' ? this.record['interventionDatetime'] : null, Validators.required],
      type: [this.data.type == 'update' ? this.record['type'] : null],
      specialty: [this.data.type == 'update' ? this.record['specialty'] : null, Validators.required],
      procedure: [this.data.type == 'update' ? this.record['procedure'] : null, Validators.required],
      doctor: [this.data.type == 'update' ? this.record['doctor'] : null, Validators.required],
      episode: [this.data.type == 'update' ? this.record['episode'] : null],
      // Texto libre; si es un registro viejo con id del catálogo, se muestra su nombre.
      operationRoom: [this.data.type == 'update' ? ((this.data['operationRoomObj'] ?? {})[this.record['operationRoom']] ?? this.record['operationRoom']) : null, Validators.required],
      diagnosis: [this.data.type == 'update' ? this.record['diagnosis'] : null, Validators.required],
      hospital: [this.data.type == 'update' ? this.record['hospital'] : null, Validators.required],

      // Campo para el request generado desde el procedure
      request: [this.data.type == 'update' ? this.record['request'] : null, Validators.required],

      // Campo para seleccionar paciente
      patient: [this.data.type == 'update' ? this.record['patient'] : null, Validators.required],

      // Campos del paciente
      patientName: [this.data.type == 'update' && this.record['patient'] ? this.patientObject[this.record['patient']]['name'] : null, Validators.required],
      curp: [this.data.type == 'update' && this.record['patient'] ? this.patientObject[this.record['patient']]['CURP'] : null],
      record: [this.data.type == 'update' && this.record['patient'] ? this.patientObject[this.record['patient']]['identifier'] : null],
      patientBirthdate: [this.data.type == 'update' && this.record['patient'] ? this.patientObject[this.record['patient']]['birthDate'] : null],
      patientSex: [this.data.type == 'update' && this.record['patient'] ? this.patientObject[this.record['patient']]['gender'] : null, Validators.required],
    });

    // Configurar el autocomplete
    this.setupAutocomplete();

    // Configurar el listener de procedure — debe ir ANTES del bloque update
    // para que el setValue de abajo sea capturado por el subscription
    this.setupProcedureListener();

    if (this.data.type == 'update') {
      this.filterData();

      if (this.record['patient']) {
        this.onPatientSelected(this.record['patient']);
        // Establecer el texto en el autocomplete
        const patientData = this.patientObject[this.record['patient']];
        if (patientData) {
          this.patientSearchControl.setValue(this.getPatientDisplayText(patientData));
        }
      }

      // ✅ Forzar la emisión del valor actual del procedure para que el listener
      // reconstruya siempre el request, sin importar si ya existía o no.
      if (this.record['procedure']) {
        this.interventionForm.get('procedure').setValue(
          this.record['procedure'],
          { emitEvent: true }
        );
      }
    }

    // Deshabilitar campos de paciente hasta que se seleccione uno
    this.togglePatientFields(false);
  }

  /**
   * @description Construye el objeto request a partir de los datos del procedure
   */
  private buildRequestFromProcedure(procedureData: any): any {
    const categories = ['products', 'equipments', 'medicines', 'instrumentals'];
    const procedureRequest = procedureData.required;
    const request: any = {};

    for (let cat of categories) {
      if (procedureRequest?.[cat]?.length) {
        request[cat] = procedureRequest[cat].map(item => {
          const product = this.productObject[item.product];
          return {
            itemCode: product?.code ?? '',
            itemDescription: product?.description ?? '',
            itemNumber: '',
            quantity: item.quantity ?? 0,
            delivery: 0,
            consumption: 0,
            refund: 0,
            type: 'BCB',
          };
        });
      }
    }

    return request;
  }

  /**
   * @description Escucha cambios en el campo procedure, construye el request y lo asigna al formulario.
   *              Si el procedure no tiene 'required', limpia el campo request.
   */
  setupProcedureListener() {
    this.interventionForm.get('procedure')?.valueChanges.subscribe(procedureId => {
      if (!procedureId) {
        // Limpiar request si no hay procedure seleccionado
        this.interventionForm.patchValue({ request: null });
        return;
      }

      const procedureData = this.procedureList.find(item => item.id === procedureId);

      if (!procedureData?.required) {
        // Limpiar request si el procedure no tiene 'required'
        this.interventionForm.patchValue({ request: null });
        return;
      }

      // Construir y asignar el request al formulario
      const request = this.buildRequestFromProcedure(procedureData);
      this.interventionForm.patchValue({ request });
    });
  }

  /**
   * @description Configura el autocomplete de pacientes
   */
  setupAutocomplete() {
    this.filteredPatients = this.patientSearchControl.valueChanges.pipe(
      startWith(''),
      map(value => {
        const searchValue = typeof value === 'string' ? value : '';
        return this._filterPatients(searchValue);
      })
    );
  }

  /**
   * @description Filtra la lista de pacientes según el texto ingresado
   */
  private _filterPatients(value: string): any[] {
    if (!value || value.trim() === '') {
      return this.patientList.slice(0, 50);
    }

    const filterValue = value.toLowerCase().trim();

    return this.patientList.filter(patient => {
      const patientData = this.patientObject[patient.id];
      if (!patientData) return false;

      const name = (patientData.name || '').toLowerCase();
      const identifier = (patientData.identifier || '').toLowerCase();
      const curp = (patientData.CURP || '').toLowerCase();

      return name.includes(filterValue) ||
        identifier.includes(filterValue) ||
        curp.includes(filterValue);
    }).slice(0, 50);
  }

  /**
   * @description Obtiene el texto a mostrar para un paciente en el autocomplete
   */
  getPatientDisplayText(patient: any): string {
    if (!patient) return '';
    if (patient.id) {
      const patientData = this.patientObject[patient.id];
      return patientData ? `${patientData.name} - ${patientData.identifier}` : '';
    }
    return `${patient.name} - ${patient.identifier}`;
  }

  /**
   * @description Maneja cuando se selecciona una opción del autocomplete
   */
  onAutocompleteSelected(event: any) {
    const patient = event.option.value;
    if (patient && patient.id) {
      this.onPatientSelected(patient.id);
      const patientData = this.patientObject[patient.id];
      if (patientData) {
        this.patientSearchControl.setValue(this.getPatientDisplayText(patientData));
      }
    }
  }

  /**
   * @description Limpia la selección del paciente
   */
  clearPatientSelection() {
    this.selectedPatient = null;
    this.patientSearchControl.setValue('');
    this.interventionForm.patchValue({
      patient: null,
      patientName: null,
      curp: null,
      record: null,
      patientBirthdate: null,
      patientSex: null
    });
    this.togglePatientFields(false);
  }

  /**
   * @description Maneja la selección de un paciente
   */
  onPatientSelected(patientId: string) {
    const patientData = this.patientObject[patientId];

    if (patientData) {
      this.selectedPatient = { id: patientId, ...patientData };
      this.isEditingPatient = false;
      this.isCreatingPatient = false;

      this.interventionForm.patchValue({
        patient: patientId,
        patientName: patientData.name,
        curp: patientData.CURP,
        record: patientData.identifier,
        patientBirthdate: patientData.birthDate,
        patientSex: patientData.gender
      });

      this.togglePatientFields(false);
    }
  }

  /**
   * @description Habilita/deshabilita los campos del paciente
   */
  togglePatientFields(enable: boolean) {
    const fields = ['patientName', 'curp', 'record', 'patientBirthdate', 'patientSex'];
    fields.forEach(field => {
      if (enable) {
        this.interventionForm.get(field).enable();
      } else {
        this.interventionForm.get(field).disable();
      }
    });
  }

  /**
   * @description Inicia la creación de un nuevo paciente
   */
  startCreatingPatient() {
    this.isCreatingPatient = true;
    this.isEditingPatient = false;
    this.selectedPatient = null;

    this.patientSearchControl.setValue('');
    this.interventionForm.patchValue({
      patient: null,
      patientName: null,
      curp: null,
      record: null,
      patientBirthdate: null,
      patientSex: null
    });

    this.togglePatientFields(true);
  }

  /**
   * @description Guarda el nuevo paciente
   */
  async saveNewPatient() {
    try {
      const patientFields = ['patientName', 'patientSex'];
      const isValid = patientFields.every(field => this.interventionForm.get(field).valid);

      if (!isValid) {
        this.customMessageService.open({
          title: 'Información',
          type: 'neutral',
          message: 'Por favor completa los campos requeridos del paciente.',
          dismissBtn: true,
        });
        return;
      }

      const patientData = {
        name: this.interventionForm.value.patientName,
        CURP: this.interventionForm.value.curp,
        identifier: this.interventionForm.value.record,
        birthDate: this.interventionForm.value.patientBirthdate,
        gender: this.interventionForm.value.patientSex,
      };

      const createConfirm = await this.customMessageService.open({
        title: 'Cuidado',
        type: 'warning',
        message: `¿Seguro que quieres crear el paciente <b>${patientData.name}</b>?`,
        acceptBtn: true,
        dismissBtn: true,
      });

      if (createConfirm) {
        patientData['operationType'] = 'single';

        const apiResponse = await this.patientsService.create(patientData);
        this.customMessageService.close();

        if (apiResponse['state'] == 'success') {
          const newPatient = apiResponse.data[0];

          this.patientList.push(newPatient);
          this.patientObject[newPatient.id] = {
            name: newPatient.name,
            CURP: newPatient.CURP,
            identifier: newPatient.identifier,
            birthDate: newPatient.birthDate,
            gender: newPatient.gender
          };

          this.interventionForm.patchValue({ patient: newPatient.id });
          this.patientSearchControl.setValue(this.getPatientDisplayText(this.patientObject[newPatient.id]));

          this.selectedPatient = { id: newPatient.id, ...this.patientObject[newPatient.id] };
          this.isCreatingPatient = false;
          this.togglePatientFields(false);
          this.setupAutocomplete();

          await this.customMessageService.open({
            title: 'Éxito',
            type: 'success',
            message: 'Paciente creado correctamente',
            duration: 2000,
          });
        } else {
          await this.customMessageService.open({
            title: 'Error',
            type: 'error',
            message: apiResponse['message'],
            duration: 2000,
          });
          throw new Error(apiResponse['message']);
        }
      }
    } catch (err) {
      this.customMessageService.close();
      await this.customMessageService.open({
        title: 'Error',
        type: 'error',
        message: String(err),
        dismissBtn: true
      });
    }
  }

  /**
   * @description Inicia la edición del paciente seleccionado
   */
  startEditingPatient() {
    if (!this.selectedPatient) {
      this.customMessageService.open({
        title: 'Información',
        type: 'neutral',
        message: 'Selecciona un paciente primero',
        dismissBtn: true,
      });
      return;
    }

    this.isEditingPatient = true;
    this.isCreatingPatient = false;
    this.togglePatientFields(true);
  }

  /**
   * @description Guarda los cambios del paciente editado
   */
  async savePatientChanges() {
    try {
      const patientFields = ['patientName', 'patientSex'];
      const isValid = patientFields.every(field => this.interventionForm.get(field).valid);

      if (!isValid) {
        this.customMessageService.open({
          title: 'Información',
          type: 'neutral',
          message: 'Por favor completa los campos requeridos del paciente.',
          dismissBtn: true,
        });
        return;
      }

      const patientData = {
        id: this.selectedPatient.id,
        name: this.interventionForm.value.patientName,
        CURP: this.interventionForm.value.curp,
        identifier: this.interventionForm.value.record,
        birthDate: this.interventionForm.value.patientBirthdate,
        gender: this.interventionForm.value.patientSex
      };

      const updateConfirm = await this.customMessageService.open({
        title: 'Cuidado',
        type: 'warning',
        message: `¿Seguro que quieres editar el paciente <b>${patientData.name}</b>?`,
        acceptBtn: true,
        dismissBtn: true,
      });

      if (updateConfirm) {
        patientData['operationType'] = 'single';

        const apiResponse = await this.patientsService.update(patientData);
        this.customMessageService.close();

        if (apiResponse['state'] == 'success') {
          const updatedPatient = apiResponse.data[0];

          this.patientObject[updatedPatient.id] = {
            name: updatedPatient.name,
            CURP: updatedPatient.CURP,
            identifier: updatedPatient.identifier,
            birthDate: updatedPatient.birthDate,
            gender: updatedPatient.gender
          };

          this.patientSearchControl.setValue(this.getPatientDisplayText(this.patientObject[updatedPatient.id]));

          this.selectedPatient = { id: updatedPatient.id, ...this.patientObject[updatedPatient.id] };
          this.isEditingPatient = false;
          this.togglePatientFields(false);
          this.setupAutocomplete();

          await this.customMessageService.open({
            title: 'Éxito',
            type: 'success',
            message: 'Paciente actualizado correctamente',
            duration: 2000,
          });
        } else {
          await this.customMessageService.open({
            title: 'Error',
            type: 'error',
            message: apiResponse['message'],
            duration: 2000,
          });
          throw new Error(apiResponse['message']);
        }
      }
    } catch (err) {
      this.customMessageService.close();
      await this.customMessageService.open({
        title: 'Error',
        type: 'error',
        message: String(err),
        dismissBtn: true
      });
    }
  }

  /**
   * @description Cancela la edición/creación del paciente
   */
  cancelPatientEdit() {
    if (this.isCreatingPatient) {
      this.clearPatientSelection();
    } else if (this.isEditingPatient && this.selectedPatient) {
      this.onPatientSelected(this.selectedPatient.id);
      const patientData = this.patientObject[this.selectedPatient.id];
      if (patientData) {
        this.patientSearchControl.setValue(this.getPatientDisplayText(patientData));
      }
    }

    this.isCreatingPatient = false;
    this.isEditingPatient = false;
    this.togglePatientFields(false);
  }

  /**
   * @description Crea un nuevo registro de intervención
   */
  public async createIntervention() {
    this.customMessageService.open({
      title: 'Espere',
      type: 'neutral',
      message: 'Creando programación quirúrgica',
      duration: 2000,
      loading: true,
    });

    if (this.interventionForm.get('interventionDatetime').invalid ||
      this.interventionForm.get('specialty').invalid ||
      this.interventionForm.get('procedure').invalid ||
      this.interventionForm.get('doctor').invalid ||
      this.interventionForm.get('hospital').invalid ||
      this.interventionForm.get('operationRoom').invalid ||
      this.interventionForm.get('patient').invalid ||
      this.interventionForm.get('request').invalid) {
      this.customMessageService.open({
        title: 'Información',
        type: 'neutral',
        message: 'Por favor completa todos los campos del registro para agregar una intervención.',
        dismissBtn: true,
      });
      return;
    }

    let data = {
      interventionDatetime: this.interventionForm.get('interventionDatetime').value,
      type: this.interventionForm.get('type').value,
      specialty: this.interventionForm.get('specialty').value,
      procedure: this.interventionForm.get('procedure').value,
      doctor: this.interventionForm.get('doctor').value,
      episode: this.interventionForm.get('episode').value,
      hospital: this.interventionForm.get('hospital').value,
      operationRoom: this.interventionForm.get('operationRoom').value,
      patient: this.interventionForm.get('patient').value,
      request: this.interventionForm.get('request').value,
      diagnosis: this.interventionForm.get('diagnosis').value,
    };

    let interventionDatetime = new Date(data['interventionDatetime']);

    if (interventionDatetime.toString() == 'Invalid Date') {
      let response = await this.customMessageService.open({
        title: 'Cuidado',
        type: 'warning',
        message: 'La fecha de intervención es incongruente.<br> Favor de revisar.',
        dismissBtn: true
      });
      if (!response) return;
    }

    let today = new Date();
    today.setHours(0);
    today.setMinutes(0);
    today.setSeconds(0);
    today.setMilliseconds(0);

    if (interventionDatetime < today) {
      let response = await this.customMessageService.open({
        title: 'Cuidado',
        type: 'warning',
        message: 'La fecha de intervención es incongruente.<br> Favor de revisar.',
        dismissBtn: true
      });
      if (!response) return;
    }

    data['contract'] = this.contractList[0]['id'];
    data['status'] = 'Pendiente';
    data['operationType'] = 'single';

    let folio = await this.interventionsService.search({
      searchType: 'length',
      condition: { deleted: false, contract: data['contract'],startDate:this.contractList[0]['startsOn'],finishDate: this.contractList[0]['endsOn']}
    });
    data['fileNumber'] = 'TME-' + String(Number(folio['data'][0]) + 1);

    let apiResponse = await this.interventionsService.create(data);
    if (apiResponse['state'] == 'success') {
      this.customMessageService.open({
        title: '¡Hecho!',
        type: 'success',
        message: apiResponse['message'],
        duration: 2000,
      });
      this.dialogRef.close(apiResponse.data[0])
    } else {
      this.customMessageService.open({
        title: 'Error',
        type: 'error',
        message: apiResponse['message'],
        duration: 2000,
      });
    }
  }

  /**
   * @description Edición de intervención
   */
  async patchInterventions() {
    this.customMessageService.open({
      title: 'Espere',
      type: 'neutral',
      message: 'Editando programación quirúrgica',
      duration: 2000,
      loading: true,
    });

    if (this.interventionForm.get('interventionDatetime').invalid ||
      this.interventionForm.get('specialty').invalid ||
      this.interventionForm.get('procedure').invalid ||
      this.interventionForm.get('doctor').invalid ||
      this.interventionForm.get('hospital').invalid ||
      this.interventionForm.get('operationRoom').invalid ||
      this.interventionForm.get('patient').invalid ||
      this.interventionForm.get('request').invalid) {
      this.customMessageService.open({
        title: 'Información',
        type: 'neutral',
        message: 'Por favor completa todos los campos del registro para editar la programación quirúrgica.',
        dismissBtn: true,
      });
      return;
    }

    let data = {
      id: this.record['id'],
      interventionDatetime: this.interventionForm.get('interventionDatetime').value,
      type: this.interventionForm.get('type').value,
      specialty: this.interventionForm.get('specialty').value,
      procedure: this.interventionForm.get('procedure').value,
      doctor: this.interventionForm.get('doctor').value,
      episode: this.interventionForm.get('episode').value,
      hospital: this.interventionForm.get('hospital').value,
      operationRoom: this.interventionForm.get('operationRoom').value,
      patient: this.interventionForm.get('patient').value,
      request: this.interventionForm.get('request').value,
      diagnosis: this.interventionForm.get('diagnosis').value,
      modifiedBy: sessionStorage.getItem('userId'),
      operationType: 'single'
    };

    let apiResponse = await this.interventionsService.update(data);
    if (apiResponse['state'] == 'success') {
      await this.customMessageService.open({
        title: '¡Hecho!',
        type: 'success',
        message: apiResponse['message'],
        duration: 2000,
      });
      this.dialogRef.close(apiResponse.data[0])
    } else {
      await this.customMessageService.open({
        title: 'Error',
        type: 'error',
        message: apiResponse['message'],
        duration: 2000,
      });
    }
  }

  /**@description Filtrar datos */
  filterData() {
    this.procedureFilteredList = this.procedureList.filter((x) => x['specialty'] == this.interventionForm.value['specialty']);
    this.doctorFilteredList = this.doctorList;
  }
}